<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    @change="editSingleSettingHandler"
    :has-buttons="!!(addressesProvider === 'system' && systemProviderProps.datasource)"
    has-validation
    :is-form-fetched="isFetched"
    :module-enabler="moduleEnabler"
    @save="save"
  >
    <template #datasource.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="systemProviderProps.datasource" @change="clearInputs" />
      </v-col>
    </template>
    <template #attribute_place.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_place"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
    <template #attribute_street.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_street"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
    <template #attribute_number.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_number"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
    <template #attribute_postal_code.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_postal_code"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

import { call, get } from 'vuex-pathify';

export default {
  name: 'AddressesSettings',
  components: { AdminWrapper, DataInput },
  data: () => ({
    isFetched: false,
    selectedSystemProviderPropsChanged: false,
    systemProviderProps: {
      settings: {
        attribute_place: '',
        attribute_street: '',
        attribute_number: '',
        attribute_postal_code: '',
      },
      datasource: '',
    },
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@ADDRESSES.verbose_name'),
    addressesProvider: get('admin/settingsValues@addresses_provider'),
    isModuleEnabled: get('admin/settingsValues@addresses_module_enabled'),
    dataSources: get('layers/metadata'),
    dataSourceSettingsName() {
      return 'addresses_datasource';
    },
    selectedDatasourceAttributes() {
      return this.systemProviderProps.datasource ? this.selectedDatasource.attributes_schema.attributes : undefined;
    },
    selectedDatasource() {
      return this.dataSources[this.systemProviderProps.datasource];
    },
    pointDataSources() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.geometry_type === 'point' && dataSource.scope === 'core' && dataSource.module === null
      );
    },
    inputs() {
      return [
        {
          name: 'addresses_provider',
          translationPath: 'admin.parcelsProvider',
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          dataType: {
            name: 'select',
          },
          items: [
            { text: this.$i18n.t('admin.gugik'), value: 'gugik_api' },
            { text: this.$i18n.t('admin.system'), value: 'system' },
          ],
        },
        ...(this.addressesProvider === 'system' ? this.systemInputs : []),
        ...(this.systemProviderProps.datasource !== '' ? this.dataSourceInputs : []),
      ];
    },
    systemInputs() {
      return [
        {
          name: 'datasource',
          translationPath: 'admin.addressesDatasourceTitle',
          dataType: {
            name: 'select',
          },
          align: 'start',
          itemValue: 'name',
          itemText: 'verbose_name',
          items: this.pointDataSources,
          subtitle: 'admin.addressesDatasourceSubtitle',
        },
      ];
    },
    dataSourceInputs() {
      return [
        {
          name: 'attribute_place',
          translationPath: 'admin.addresses_cityTitle',
          subtitle: 'admin.addresses_citySubtitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          itemText: 'verbose_name',
          itemValue: 'name',
          items: this.selectedDatasourceAttributes,
        },
        {
          name: 'attribute_street',
          translationPath: 'admin.addresses_streetTitle',
          subtitle: 'admin.addresses_streetSubtitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          itemText: 'verbose_name',
          itemValue: 'name',
          items: this.selectedDatasourceAttributes,
        },
        {
          name: 'attribute_number',
          translationPath: 'admin.addresses_streetNumberTitle',
          subtitle: 'admin.addresses_streetNumberSubtitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          itemText: 'verbose_name',
          itemValue: 'name',
          items: this.selectedDatasourceAttributes,
        },
        {
          name: 'attribute_postal_code',
          translationPath: 'admin.addresses_zipCodeTitle',
          subtitle: 'admin.addresses_zipCodeSubtitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          itemText: 'verbose_name',
          itemValue: 'name',
          items: this.selectedDatasourceAttributes,
        },
      ];
    },
    moduleEnabler() {
      return {
        name: 'addresses_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    getDataSources: call('layers/getLayersMetadata'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getModulesMapping: call('admin/getModulesMapping'),
    async editSingleSettingHandler(value, name) {
      if (name === 'addresses_provider') {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      } else {
        return;
      }
    },
    async save() {
      if (!this.selectedSystemProviderPropsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.systemProviderProps };
      try {
        await this.editDatasourceSettings(payload);
        this.selectedSystemProviderPropsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        console.log('  error', error);
      }
    },
    async updateSelectedDataSource() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.systemProviderProps = {
          datasource,
          settings,
        };
        this.selectedSystemProviderPropsChanged = false;
      } catch {
        this.systemProviderProps = {
          settings: {
            attribute_place: '',
            attribute_street: '',
            attribute_number: '',
            attribute_postal_code: '',
          },
          datasource: '',
        };
        this.selectedSystemProviderPropsChanged = false;
      }
    },
    async init() {
      await Promise.all([this.getModulesMapping(), this.getDataSources({ with_attributes_schema: true })]);
      if (this.addressesProvider === 'system' && this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFetched = true;
    },
    clearInputs() {
      this.systemProviderProps.settings.attribute_place = '';
      this.systemProviderProps.settings.attribute_street = '';
      this.systemProviderProps.settings.attribute_number = '';
      this.systemProviderProps.settings.attribute_postal_code = '';
      this.selectedSystemProviderPropsChanged = true;
    },
  },
  watch: {
    addressesProvider(nV) {
      if (nV === 'system' && this.isModuleEnabled) {
        this.updateSelectedDataSource();
      } else {
        this.systemProviderProps.datasource = '';
      }
    },
    isModuleEnabled(nV) {
      if (nV) {
        this.isFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFetched = true;
    }
  },
};
</script>
